import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { AllPlanScheduleType, ChatSessionType, RoutineActivity, TaskItemForSchedule } from '@demind-inc/core';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { useAuthContext } from '../AuthProvider';
import { isDevelopment, submitRatingAIForm, useFeatureFlag } from '../../../utils';
import {
  buildDebugMockConversationMessages,
  buildDebugMockPlanStatusConversationMessages,
  buildDebugMockReplanConversationMessages,
  DEBUG_MOCK_PLAN_STATUS_LINES,
} from './debugMockData';

import { TalkWithAssistantUserContext } from '../../api';

import { ResponseRating } from '../../../components';
import {
  aiSchedulerChatMessagesAtom,
  aiSchedulerChatSessionIdAtom,
  assistantChatFeedbackAtom,
  assistantChatProcessedIdsAtom,
  isAllPlanPanelOpenAtom,
  planMyDayIntroSeedDateAtom,
  selectedDateAtom,
  sidebarOpenAtom,
  suggestAllAIPlanStateAtom,
} from '../../recoil';
import { useAllPlanSuggestedModal } from '../../../hooks';
import { useDailyPlanJobRecovery } from '../../hooks/useDailyPlanJobRecovery';
import { useWeeklyPlanJobRecovery } from '../../hooks/useWeeklyPlanJobRecovery';
import { useChatState, usePlanEntryHandler, useRecommendationHandlers } from './hooks';
import {
  ASSISTANT_EXPLA_CHAT_START_MESSAGE,
  ASSISTANT_PLAN_CHAT_START_MESSAGE,
  assistantPlanEntryButtons,
} from '../assistant.constant';
import { AssistantChatContextType, AssistantChatProviderProps, HandlePlanEntryRes } from './types';
import { DEFAULT_LOADING_STATUS_TEXT } from './constants';

const AssistantChatContext = createContext<AssistantChatContextType | undefined>(undefined);

export const AssistantChatProvider: React.FC<AssistantChatProviderProps> = ({ children }) => {
  const { user } = useAuthContext();
  const location = useLocation();
  const { t } = useTranslation();
  // Kill-switch for the v4 AI chat overhaul (Suggested Blocks UX, Plan My Day
  // chat bubbles) — see startPlanMyDayIntro/ensurePlanPreviewMessage below.
  const { featureFlags } = useFeatureFlag();
  const isV4Enabled = featureFlags.FF_lifestack_v4 ?? true;
  const feedbackData = useRecoilValue(assistantChatFeedbackAtom);
  const selectedDate = useRecoilValue(selectedDateAtom);
  const setProcessedIds = useSetRecoilState(assistantChatProcessedIdsAtom);
  const setSchedulerChatMessages = useSetRecoilState(aiSchedulerChatMessagesAtom);
  const setSchedulerChatSessionId = useSetRecoilState(aiSchedulerChatSessionIdAtom);
  const [isAllPlanPanelOpen, setIsAllPlanPanelOpen] = useRecoilState(isAllPlanPanelOpenAtom);
  const setPlanMyDayIntroSeedDate = useSetRecoilState(planMyDayIntroSeedDateAtom);
  const setSuggestAllAIPlanState = useSetRecoilState(suggestAllAIPlanStateAtom);
  const setSidebarOpen = useSetRecoilState(sidebarOpenAtom);
  const [openAssistantChat, setOpenAssistantChat] = useState(true);
  const {
    rerunSuggestionWithTypes,
    isLoading: isAllPlanLoading,
    targetDate: allPlanTargetDate,
    summary: allPlanSummary,
    cancelActivePlanGeneration,
  } = useAllPlanSuggestedModal();
  // Resumes a daily-plan job that was still generating when the tab was
  // reloaded or closed — mounted once here so it runs exactly once per app
  // load, independent of which screen the user lands on.
  useDailyPlanJobRecovery();
  // State management
  const {
    state,
    updateState,
    addMessage,
    addMessages,
    insertMessageBeforeId,
    removeMessages,
    updateMessage,
    clearMessages,
    addAbortController,
    clearAbortControllers,
    removeTemporalSuggestedEvent,
    setTemporalSuggestedEvents,
  } = useChatState();
  // Same for a plan-my-week job (persisted by runAIWeekPlanStream). The
  // recovered atoms alone don't bring the scheduler chat panel back — other
  // mount effects (e.g. ChatSideBar's startPlanChat) default back to plain
  // chat mode, which is what hides the week plan's status/results after a
  // reload. Reopening scheduler mode here runs after those (this provider is
  // an ancestor, so its effects commit after its children's) and wins.
  useWeeklyPlanJobRecovery({
    onResume: () => {
      setSidebarOpen(true);
      setOpenAssistantChat(true);
      updateState({ isSchedulerMode: true, schedulerScope: 'week' });
    },
  });

  // Business logic handlers - using new streaming flow
  const {
    handlePlanEntry: handlePlanEntryStreaming,
    handlePlanChat: handlePlanChatStreaming,
    sendMessage: sendMessageStreaming,
    sendQuestionAnswer,
    isStreaming,
    stopStreaming,
    stopChat: stopChatStreaming,
  } = usePlanEntryHandler({
    state,
    updateState,
    addMessage,
    updateMessage,
    addAbortController,
    setTemporalSuggestedEvents,
  });

  const {
    startRecommendationFlow,
    handleFeelingSelection,
    handleActivitySelection,
    sendRecommendation,
  } = useRecommendationHandlers(state, updateState, addMessage, addAbortController, (text) =>
    handlePlanChatStreaming('recommendation', text)
  );

  const targetDate = useMemo(
    () => (selectedDate.isSameOrBefore(dayjs(), 'day') ? dayjs() : selectedDate),
    [selectedDate]
  );

  // Use new streaming message handler
  const sendMessage = sendMessageStreaming;

  // Chat initialization methods
  const startPlanChat = useCallback(
    (specificTargetDate?: string) => {
      updateState({
        messages: [ASSISTANT_PLAN_CHAT_START_MESSAGE],
        isUserTurn: true,
        loading: false,
        loadingStatusText: [],
        isSchedulerMode: false,
        schedulerScope: 'day',
      });
    },
    [updateState, targetDate.get('date')]
  );

  const startExplanationChat = useCallback(
    (sessionType: ChatSessionType) => {
      updateState({
        messages: [ASSISTANT_EXPLA_CHAT_START_MESSAGE(sessionType)],
        isUserTurn: true,
        loading: false,
        loadingStatusText: [],
        isSchedulerMode: false,
        schedulerScope: 'day',
        currentSessionType: sessionType,
      });
    },
    [updateState]
  );

  const startGeneralChat = useCallback(
    (text: string) => {
      updateState({
        isUserTurn: true,
        loading: false,
        loadingStatusText: [],
        isSchedulerMode: false,
        schedulerScope: 'day',
        currentSessionType: 'general',
      });
      sendMessage(text, 'general');
    },
    [updateState, addMessage, sendMessage]
  );

  const startSchedulerChat = useCallback(
    (scope: 'day' | 'week' = 'day') => {
      updateState({ isSchedulerMode: true, schedulerScope: scope });
    },
    [updateState]
  );

  const exitSchedulerMode = useCallback(() => {
    updateState({ isSchedulerMode: false, schedulerScope: 'day' });
  }, [updateState]);

  // The "Here's your updated plan" summary text is only inserted once
  // generation finishes — this ref holds the preview message id to insert it
  // before, so it lands in the right spot instead of appending after
  // whatever's been added meanwhile.
  const pendingPlanSummaryBeforeIdRef = useRef<string | null>(null);
  // True for the span of an actual rerunSuggestionWithTypes call below — set
  // synchronously before that call starts, so ensurePlanPreviewMessage can
  // reliably tell "a fresh generation is starting" apart from "isLoading
  // just hasn't flipped to true yet" (isLoading itself lags behind the call
  // by a tick, so checking it alone races and can reveal the summary over an
  // empty/loading card).
  const isStartingFreshPlanGenerationRef = useRef(false);

  const revealPendingPlanSummary = useCallback(() => {
    const beforeMessageId = pendingPlanSummaryBeforeIdRef.current;
    if (!beforeMessageId) {
      return;
    }
    pendingPlanSummaryBeforeIdRef.current = null;
    insertMessageBeforeId(
      {
        messageId: `all_plan_preview_summary_${Date.now()}`,
        content: allPlanSummary || t('task.allPlan.chatIntro'),
        role: 'assistant',
        sentAt: new Date().toISOString(),
      },
      beforeMessageId
    );
  }, [insertMessageBeforeId, t, allPlanSummary]);

  // Safety net for triggers that don't explicitly call revealPendingPlanSummary
  // themselves (e.g. an external isAllPlanPanelOpenAtom trigger firing while a
  // generation kicked off elsewhere is still running) — reveals once
  // generation finishes.
  useEffect(() => {
    if (!isAllPlanLoading) {
      revealPendingPlanSummary();
    }
  }, [isAllPlanLoading, revealPendingPlanSummary]);

  // Adds the live all-plan preview bubble to the thread if one isn't already
  // there — its content is driven by useAllPlanSuggestedModal(), so a second
  // trigger just reuses the existing bubble instead of duplicating it. The
  // "Here's your updated plan" summary text is intentionally held back until
  // generation completes (see revealPendingPlanSummary) — showing it while
  // the plan is still streaming reads as done when it isn't.
  const ensurePlanPreviewMessage = useCallback(() => {
    if (!isV4Enabled) {
      addMessage({
        messageId: `all_plan_preview_unavailable_${Date.now()}`,
        content: t('assistantChat.v4Unavailable'),
        role: 'assistant',
        sentAt: new Date().toISOString(),
      });
      return;
    }

    // Keep the active plan context in sync with the target date every time
    // this runs (not just on first creation) — e.g. triggering a review again
    // for a plan generated against a different date than the one a stale
    // preview bubble was created for.
    updateState({
      activePlanContext: { targetDate: allPlanTargetDate || dayjs().format('YYYY-MM-DD') },
    });

    const hasPreviewMessage = state.messages.some((message) => message.allPlanPreview);
    if (hasPreviewMessage) {
      return;
    }
    // No "Run" user bubble — status text under the intro/previous turn is
    // enough feedback while generation is in flight.
    const previewMessageId = `all_plan_preview_${Date.now()}`;
    addMessage({
      messageId: previewMessageId,
      content: '',
      role: 'assistant',
      sentAt: new Date().toISOString(),
      allPlanPreview: true,
    });
    pendingPlanSummaryBeforeIdRef.current = previewMessageId;
    // Only reveal synchronously here when nothing is actively kicking off a
    // fresh generation (isStartingFreshPlanGenerationRef is set synchronously
    // by generatePlanFromIntro *before* it triggers this effect, so it's
    // reliable here — unlike isAllPlanLoading, which can still read false for
    // a beat after a generation call starts). This covers "reviewing an
    // already-finished plan" (nothing generating). The fresh-generation case
    // instead reveals explicitly once its await resolves, with the
    // isAllPlanLoading transition effect above as a safety net.
    if (!isAllPlanLoading && !isStartingFreshPlanGenerationRef.current) {
      revealPendingPlanSummary();
    }
  }, [
    state.messages,
    addMessage,
    t,
    isV4Enabled,
    isAllPlanLoading,
    revealPendingPlanSummary,
    updateState,
    allPlanTargetDate,
  ]);

  // isAllPlanPanelOpenAtom is a one-shot trigger set from anywhere in the app
  // (FAB, daily analysis, sleep/energy edits, core action CTA, AllPlanPanel's
  // own "refresh" button, etc.) — consumed here so every one of those call
  // sites keeps working unchanged while the presentation moves from a
  // dedicated sidebar panel into this chat feed.
  useEffect(() => {
    if (!isAllPlanPanelOpen) {
      return;
    }
    setIsAllPlanPanelOpen(false);
    setSidebarOpen(true);
    setOpenAssistantChat(true);
    ensurePlanPreviewMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllPlanPanelOpen]);

  // "Plan my day" no longer jumps straight into generation — it first asks
  // "what would you like to plan today?" via an inline PlanIntroBubble.
  // Generation only runs once the user taps "Generate plan" there (see
  // generatePlanFromIntro below). targetDate seeds the bubble's date picker
  // (e.g. a "replan for this specific day" trigger) via
  // planMyDayIntroSeedDateAtom, read by PlanIntroBubble itself.
  // Always starts a fresh thread so Plan my day reads as the first turn,
  // not an append onto whatever conversation was already open.
  const startPlanMyDayIntro = useCallback(
    (targetDate?: string) => {
      state.abortControllers.forEach((controller) => {
        controller.abort();
      });
      clearAbortControllers();
      stopStreaming();
      clearMessages();
      setProcessedIds([]);
      exitSchedulerMode();

      setSidebarOpen(true);
      setOpenAssistantChat(true);
      addMessage({
        messageId: `plan_my_day_intro_user_${Date.now()}`,
        content: t('planMyDayIntro.confirmCta'),
        role: 'user',
        sentAt: new Date().toISOString(),
      });

      if (!isV4Enabled) {
        addMessage({
          messageId: `plan_my_day_intro_unavailable_${Date.now()}`,
          content: t('assistantChat.v4Unavailable'),
          role: 'assistant',
          sentAt: new Date().toISOString(),
        });
        updateState({ isUserTurn: true, loading: false, loadingStatusText: [] });
        return;
      }

      setPlanMyDayIntroSeedDate(targetDate ?? null);
      addMessage({
        messageId: `plan_my_day_intro_card_${Date.now()}`,
        content: '',
        role: 'assistant',
        sentAt: new Date().toISOString(),
        planIntro: true,
      });
      updateState({ isUserTurn: true, loading: false, loadingStatusText: [] });
    },
    [
      addMessage,
      clearAbortControllers,
      clearMessages,
      exitSchedulerMode,
      isV4Enabled,
      setOpenAssistantChat,
      setPlanMyDayIntroSeedDate,
      setProcessedIds,
      setSidebarOpen,
      state.abortControllers,
      stopStreaming,
      t,
      updateState,
    ]
  );

  const generatePlanFromIntro = useCallback(
    async (
      typesToRun: AllPlanScheduleType[],
      focusAreas: string[],
      targetDateStr: string,
      selection?: { todos?: TaskItemForSchedule[]; routine?: RoutineActivity[] }
    ) => {
      if (!typesToRun.length || !isV4Enabled) {
        return;
      }
      isStartingFreshPlanGenerationRef.current = true;
      // Show the preview bubble (with its loading hero) immediately, before
      // awaiting generation — matches ensurePlanPreviewMessage's own
      // isStartingFreshPlanGenerationRef check, which otherwise assumes
      // "reviewing an already-finished plan" and reveals the summary too early.
      ensurePlanPreviewMessage();
      try {
        await rerunSuggestionWithTypes(typesToRun, selection, {
          focusAreas,
          targetDate: targetDateStr,
        });
      } finally {
        isStartingFreshPlanGenerationRef.current = false;
      }
      revealPendingPlanSummary();
    },
    [rerunSuggestionWithTypes, ensurePlanPreviewMessage, revealPendingPlanSummary, isV4Enabled]
  );

  // Plan entry handler - using new streaming flow
  const handlePlanEntry = useCallback(
    async (
      planType: ChatSessionType,
      specificTargetDate?: string,
      prompt?: string
    ): Promise<HandlePlanEntryRes | undefined> => {
      if (!user) {
        return;
      }

      const userMessage = {
        messageId: `${planType}_${String(state.messages.length + 1)}`,
        content:
          prompt ?? assistantPlanEntryButtons.find((button) => button.id === planType)?.text ?? '',
        role: 'user' as const,
        sentAt: new Date().toISOString(),
      };

      addMessage(userMessage);
      updateState({
        currentSessionType: planType,
        isUserTurn: false,
        loading: true,
        loadingStatusText: [],
        // A fresh entry point supersedes whatever plan-my-day/replan result
        // was previously active — ensurePlanPreviewMessage sets this again
        // once its own result is ready.
        activePlanContext: null,
      });

      try {
        switch (planType) {
          case 'scheduler':
            startSchedulerChat(prompt?.toLowerCase().includes('week') ? 'week' : 'day');
            break;
          case 'recommendation':
            startRecommendationFlow();
            break;
          default:
            // Use new streaming flow for sleep, meal, task
            await handlePlanEntryStreaming(planType, specificTargetDate, prompt);
            break;
        }
      } catch (error) {
        const errorMessage = {
          messageId: String(state.messages.length + 2),
          content: 'Sorry, I encountered an error while planning. Please try again.',
          role: 'assistant' as const,
          sentAt: new Date().toISOString(),
        };
        addMessage(errorMessage);
      }

      return;
    },
    [
      user,
      state.messages.length,
      updateState,
      addMessage,
      handlePlanEntryStreaming,
      startSchedulerChat,
      startRecommendationFlow,
    ]
  );

  const responseRating = useCallback(
    (rating: ResponseRating) => {
      if (feedbackData && rating) {
        submitRatingAIForm({
          userId: user.userId,
          rating,
          actionType: feedbackData.sessionType || '',
          appApiRes: feedbackData.appApiRes || '',
          preferences: feedbackData.preferences || '',
          currentTime: feedbackData.currentTime || '',
          scienceApiReq: feedbackData.scienceApiReq || '',
          scienceApiRes: feedbackData.scienceApiRes || '',
          prompt: feedbackData.prompt || '',
          sessionId: feedbackData.sessionId || '',
        });
      }
    },
    [feedbackData, user.userId]
  );

  const stopChat = useCallback(() => {
    state.abortControllers.forEach((controller) => {
      controller.abort();
    });
    clearAbortControllers();
    stopChatStreaming();
    // Plan My Day generation lives outside the chat stream.
    if (isAllPlanLoading) {
      cancelActivePlanGeneration();
      // Drop the half-built preview and its held-back summary so a cancelled
      // run doesn't leave "updated plan" / "no suggestions" cards behind.
      pendingPlanSummaryBeforeIdRef.current = null;
      removeMessages(
        (message) =>
          !!message.allPlanPreview || message.messageId.startsWith('all_plan_preview_summary_')
      );
    }
  }, [
    cancelActivePlanGeneration,
    removeMessages,
    clearAbortControllers,
    isAllPlanLoading,
    state.abortControllers,
    stopChatStreaming,
  ]);

  // Abort current requests
  const abortCurrentRequests = useCallback(() => {
    state.abortControllers.forEach((controller) => {
      controller.abort();
    });
    clearAbortControllers();
  }, [state.abortControllers, clearAbortControllers]);

  // UI control methods
  const handleClearMessages = useCallback(() => {
    abortCurrentRequests();
    stopStreaming();
    // Stop plan-my-day generation before clearing — otherwise status/schedules
    // keep writing into Recoil after New Chat.
    cancelActivePlanGeneration();
    clearMessages();
    // Scheduler mode keeps its thread (incl. pending question cards) in its own
    // atoms, so they must be reset too or the card survives New Chat.
    setSchedulerChatMessages([]);
    setSchedulerChatSessionId(null);
    setProcessedIds([]);
    exitSchedulerMode();

    // Return to general chat
    startPlanChat();
  }, [
    abortCurrentRequests,
    stopStreaming,
    cancelActivePlanGeneration,
    clearMessages,
    setSchedulerChatMessages,
    setSchedulerChatSessionId,
    setProcessedIds,
    exitSchedulerMode,
    startPlanChat,
  ]);

  const injectDebugMockConversation = useCallback(() => {
    if (!isDevelopment) {
      return;
    }
    addMessages(buildDebugMockConversationMessages());
  }, [addMessages]);

  const injectDebugMockReplanConversation = useCallback(() => {
    if (!isDevelopment) {
      return;
    }
    addMessages(buildDebugMockReplanConversationMessages());
  }, [addMessages]);

  const injectDebugMockPlanStatusConversation = useCallback(() => {
    if (!isDevelopment) {
      return;
    }
    // Drive PlanPreviewBubble's scheduler-chat-ui__status_messages stack with
    // canned status lines (same atom the live Plan My Day stream writes).
    // Stagger lines so the bottom→top stack push animation is visible.
    setSuggestAllAIPlanState((prev) => ({
      ...prev,
      isLoading: true,
      statusLines: [DEBUG_MOCK_PLAN_STATUS_LINES[0]],
      schedules: [],
      summary: null,
      error: null,
      acceptedIds: [],
      autoAcceptedIds: [],
      discardedIds: [],
      targetDate: dayjs().format('YYYY-MM-DD'),
    }));
    addMessages(buildDebugMockPlanStatusConversationMessages());

    DEBUG_MOCK_PLAN_STATUS_LINES.slice(1).forEach((line, index) => {
      window.setTimeout(() => {
        setSuggestAllAIPlanState((prev) => ({
          ...prev,
          isLoading: true,
          statusLines: [...prev.statusLines, line].slice(-3),
        }));
      }, (index + 1) * 900);
    });
  }, [addMessages, setSuggestAllAIPlanState]);

  const contextValue: AssistantChatContextType = {
    messages: state.messages,
    isUserTurn: state.isUserTurn,
    loading: state.loading,
    loadingStatusText: state.loadingStatusText,
    isStreaming,
    temporalSuggestedEvents: state.temporalSuggestedEvents,
    chatSessionId: state.chatSessionId,
    isSchedulerMode: state.isSchedulerMode,
    targetEventOnCalendar: state.targetEventOnCalendar,
    targetDate,
    currentSessionType: state.currentSessionType,
    schedulerScope: state.schedulerScope,
    recommendationState: state.recommendationState,
    startGeneralChat,
    startPlanChat,
    startExplanationChat,
    // A reply typed while a plan-my-day/replan-my-day result is the latest
    // thread context stays scoped to that result's date instead of silently
    // falling back to a 'general' turn for today (see activePlanContext).
    sendMessage: (text: string) =>
      state.activePlanContext
        ? sendMessageStreaming(text, 'scheduler_chat', state.activePlanContext.targetDate)
        : sendMessageStreaming(text),
    sendQuestionAnswer,
    handlePlanEntry,
    clearMessages: handleClearMessages,
    setLoading: (loading: boolean) =>
      updateState({
        loading,
        ...(loading ? {} : { loadingStatusText: [] }),
      }),
    setIsUserTurn: (isUserTurn: boolean) => updateState({ isUserTurn }),
    startSchedulerChat,
    exitSchedulerMode,
    setUserContext: (userContext: TalkWithAssistantUserContext | null) =>
      updateState({ userContext }),
    responseRating,
    abortCurrentRequests,
    stopChat,
    removeTemporalSuggestedEvent,
    setTemporalSuggestedEvents,
    // Recommendation handlers
    handleFeelingSelection,
    handleActivitySelection,
    sendRecommendation,
    openAssistantChat,
    setOpenAssistantChat,
    startPlanMyDayIntro,
    generatePlanFromIntro,
    isAllPlanLoading,
    injectDebugMockConversation,
    injectDebugMockReplanConversation,
    injectDebugMockPlanStatusConversation,
  };

  return (
    <AssistantChatContext.Provider value={contextValue}>{children}</AssistantChatContext.Provider>
  );
};

export const useAssistantChat = (): AssistantChatContextType => {
  const context = useContext(AssistantChatContext);
  if (context === undefined) {
    throw new Error('useAssistantChat must be used within an AssistantChatProvider');
  }
  return context;
};
